import React, { useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import type { BillingRecord } from "@services/billing";

type WelcomeBackDialogProps = {
  open: boolean;
  record: BillingRecord | null;
  onGoToBilling: () => void;
};

function addMonths(date: Date, months: number): Date {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

function formatShortDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Design-preview restore metrics until a resubscribe-impact API exists. */
const RESTORED_STATS = {
  complianceRecords: "47 records",
  openItems: "14 items",
} as const;

export const WelcomeBackDialog: React.FC<WelcomeBackDialogProps> = ({
  open,
  record,
  onGoToBilling,
}) => {
  const facilityName = record?.facilityName || "Facility";
  const plan = record?.selectedPlan || "Plan";
  const frequency = record?.billingFrequency || "Monthly";

  const nextBillingDate = useMemo(() => {
    const start = new Date();
    const isYearly = frequency.toLowerCase() === "yearly";
    return formatShortDate(addMonths(start, isYearly ? 12 : 1));
  }, [frequency, open]);

  const restoredRows = [
    { label: "Compliance records", value: RESTORED_STATS.complianceRecords },
    { label: "Open items", value: RESTORED_STATS.openItems },
    { label: "Next billing date", value: nextBillingDate },
    { label: "Plan", value: `${plan} (${frequency})` },
  ] as const;

  return (
    <Dialog
      open={open}
      onClose={onGoToBilling}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          fontFamily: '"Rethink Sans", sans-serif',
          overflow: "hidden",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            backgroundColor: "#f3fdff",
            px: { xs: 2.5, sm: 3 },
            py: 3,
            textAlign: "center",
            borderBottom: "1px solid #d8eff4",
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              backgroundColor: "#dff6dd",
              border: "1px solid #107c10",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 1.5,
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 28, color: "#107c10" }} />
          </Box>
          <Typography
            sx={{
              fontSize: "22px",
              fontWeight: 700,
              color: "#107c10",
              lineHeight: 1.3,
              mb: 1,
            }}
          >
            Welcome back
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#525252",
              lineHeight: 1.5,
              maxWidth: 480,
              mx: "auto",
            }}
          >
            {facilityName} is active again. All compliance records, deadlines,
            and history have been fully restored. A confirmation email has been
            sent to all facility admins.
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5 }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#262626",
              mb: 1.5,
            }}
          >
            Restored
          </Typography>

          <Box
            sx={{
              border: "1px solid #d8eff4",
              borderRadius: "8px",
              overflow: "hidden",
              mb: 2,
            }}
          >
            {restoredRows.map((row, index) => (
              <Box
                key={row.label}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
                  gap: { xs: 0.25, sm: 2 },
                  px: 2,
                  py: 1.25,
                  backgroundColor: index % 2 === 0 ? "#f8fdfe" : "#ffffff",
                  borderBottom:
                    index < restoredRows.length - 1 ? "1px solid #e5f5f8" : "none",
                }}
              >
                <Typography sx={{ fontSize: "13px", color: "#737373" }}>
                  {row.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#262626",
                    textAlign: { xs: "left", sm: "right" },
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              type="button"
              variant="outlined"
              onClick={onGoToBilling}
              startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
              sx={{
                textTransform: "none",
                fontFamily: '"Rethink Sans", sans-serif',
                fontWeight: 600,
                fontSize: "14px",
                color: "#404040",
                borderColor: "#b3b3b3",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                px: 3,
                py: 1.1,
                "&:hover": {
                  borderColor: "#8a8a8a",
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              Go to billing
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeBackDialog;
